import { Card, Stack, Inline, Heading, breakpoints, vars } from '@etg/wings';
import { Pictogram } from '@eti/components';
import { useEffect, useState } from 'react';
import { css, cx, mediaQueries } from '@eti/styles';
import { useTranslation, useProperty, useSiteContext } from '@eti/providers';
import MobileAppStore from './MobileAppStore';

const GOOGLE_PLAY = 'googleplay';
const APP_STORE = 'appstore';
const IOS = 'iOS';
const ANDROID = 'Android';

const fetchStoreImage = async (store: string) => {
  try {
    const src = await import(
      /* webpackMode: "lazy-once" */ `@eti/assets/logos/store-badges/${store}-badge.svg`
    );
    return src.default;
  } catch {
    return null;
  }
};

const getMobileOperatingSystem = () => {
  const { userAgent } = navigator;

  if (/android/i.test(userAgent)) {
    return ANDROID;
  }

  if (/iPad|iPhone/.test(userAgent)) {
    return IOS;
  }

  return null;
};

const mobileAppHeaderStyles = css`
  font-size: 1.25rem;

  @media ${mediaQueries.large.up} {
    font-size: 1.375rem;
  }
`;

const cardStyles = css`
  background-color: ${vars.colors.promotional.background};
  position: relative;
`;

const pictogramStyles = css`
  bottom: 0;
  display: none;
  position: absolute;
  right: 0;

  @media ${mediaQueries.large.up} {
    display: block;
  }

  [dir='rtl'] & {
    left: 0;
    right: unset;
    transform: scale(-1, 1);
  }
`;

const stackStyles = css`
  position: relative;
`;

const ulStackStyles = css`
  margin-left: 24px;

  & li {
    list-style-type: disc;
  }

  [dir='rtl'] & {
    margin-left: 0;
    margin-right: 24px;
  }
`;

const contentStyles = css`
  font-size: 0.875rem;
  line-height: 24px;

  @media ${mediaQueries.large.up} {
    font-size: 1rem;
  }
`;
interface MobileAppBannerProps {
  className?: string;
  isStartPage?: boolean;
}

const MobileAppBanner = ({ className, isStartPage = false }: MobileAppBannerProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { brand } = useSiteContext();

  const appStoreLink = p('Order.DownloadApp.AppStoreLink');
  const playStoreLink = p('Order.DownloadApp.PlayStoreLink');

  const [googlePlayImage, setGooglePlayImage] = useState('');
  const [appStoreImage, setAppStoreImage] = useState('');

  useEffect(() => {
    const loadImages = async () => {
      const googlePlaySrc = await fetchStoreImage(GOOGLE_PLAY);
      setGooglePlayImage(googlePlaySrc);

      const appStoreSrc = await fetchStoreImage(APP_STORE);
      setAppStoreImage(appStoreSrc);
    };
    loadImages();
  }, [brand]);

  const userOperatingSystem = getMobileOperatingSystem();

  return (
    <Card
      className={cx(cardStyles, className)}
      padding={{ [breakpoints._0]: 16, [breakpoints._560]: 24 }}
    >
      <Pictogram
        brand={brand.code}
        className={pictogramStyles}
        data-testid={`${isStartPage ? 'startPage' : 'resultPage'}-mobileApp-banner`}
        height={isStartPage ? undefined : 150}
        id="mobile-app"
        width={isStartPage ? undefined : 291}
      />
      <Stack className={stackStyles} spacing={16}>
        <Heading className={mobileAppHeaderStyles} level={2}>
          {t('MobileAppPromoBanner.Header', brand.name)}
        </Heading>
        <Stack className={contentStyles} spacing={0}>
          <span>{t('MobileAppPromoBanner.List.Label')}</span>
          <Stack as="ul" className={ulStackStyles} spacing={0}>
            <li>{t('MobileAppPromoBanner.ListItem1.Text')}</li>
            <li>{t('MobileAppPromoBanner.ListItem2.Text')}</li>
            <li>{t('MobileAppPromoBanner.ListItem3.Text')}</li>
          </Stack>
        </Stack>
        <Inline spacing={16}>
          {!userOperatingSystem ? (
            <>
              <MobileAppStore imageSrc={appStoreImage} store={APP_STORE} url={appStoreLink} />
              <MobileAppStore imageSrc={googlePlayImage} store={GOOGLE_PLAY} url={playStoreLink} />
            </>
          ) : (
            <>
              {userOperatingSystem === IOS && (
                <MobileAppStore imageSrc={appStoreImage} store={APP_STORE} url={appStoreLink} />
              )}
              {userOperatingSystem === ANDROID && (
                <MobileAppStore
                  imageSrc={googlePlayImage}
                  store={GOOGLE_PLAY}
                  url={playStoreLink}
                />
              )}
            </>
          )}
        </Inline>
      </Stack>
    </Card>
  );
};

export default MobileAppBanner;
